/** @module components/drag-and-drop-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import View from '@lernetz/ts-lib/src/core/view/View';

import {Item} from './Item';

import {DropArea as dropAreaTemplate} from '../../templates/drag-and-drop-quiz';


/**
 * Specifies the initial state for a [[DropArea]].
 */
export interface DropAreaInitialState {

    /**
     * Sets the items that should appear as initially dropped onto this drop area.
     */
    items: Item[];

    /**
     * Sets an optional maximum number of draggable items allowed in this drop area.
     * If not set, the number of items that can be dropped in this area is unlimited.
     */
    maxItemCount?: number;
}


/**
 * A drop area in a [[DragAndDropQuiz]] onto which [[Item]]s can be dropped.
 */
export class DropArea extends Model {

    /**
     * Gets whether the drop area is currently empty.
     */
    isEmpty: boolean;

    /**
     * Gets the items that should appear as currently dropped onto this drop area.
     *
     * A [[change]] event is raised when this property's value changes.
     *
     * Do not modify this collection directly. Instead, call [[onItemDropped]] and [[onItemRemoved]]
     * to let this class perform an appropriate update for items.
     */
    public readonly items: Item[];

    /**
     * Gets the maximum number of draggable items allowed in this drop area, or `false`
     * if the number of draggable items is not limited.
     */
    public readonly maxItemCount: number | false;

    /**
     * Initializes a new instance of the [[DropArea]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: DropAreaInitialState, data: object = {}) {
        super({
            isEmpty: !initialState.items || !initialState.items.length,
            items: [],
            maxItemCount: false,
            ...initialState,
            ...data
        });
    }

    /**
     * Asks the drop area whether it accepts the specified draggable item.
     *
     * Returns `true` if either the maximum allowed number of items ([[maxItemCount]])
     * is not limited, or there are currently less items in this drop area than are allowed.
     *
     * @param item The item which wants to be dropped in this area.
     */
    public canDropItem(item: Item): boolean {
        const maxItemCount = this.maxItemCount;
        return !maxItemCount || this.items.length < maxItemCount;
    }

    /**
     * Gets a CSS selector identifying the template element where draggable items are rendered.
     *
     * Defaults to `'.items'`.
     *
     * Override this method if you are using a custom template for which
     * this default is not suitable.
     */
    public getItemContainerSelector(): string {
        return '.items';
    }

    /**
     * Notifies this drop area that the specified item has been dropped here.
     *
     * @param item The item that has been dropped in this area.
     */
    public onItemDropped(item: Item): void {
        const items = this.items;
        const itemIndex = items.indexOf(item);
        if (itemIndex < 0) {
            this.items.push(item);
            this.isEmpty = false;
            this.change.dispatch({ name: 'items', newValue: items, oldValue: items });
        }
    }

    /**
     * Notifies this drop area that the specified item has been removed from here.
     *
     * @param item The item that has been removed from this area.
     */
    public onItemRemoved(item: Item): void {
        const items = this.items;
        const itemIndex = items.indexOf(item);
        if (itemIndex >= 0) {
            items.splice(itemIndex, 1);
            this.isEmpty = !this.items.length;
            this.change.dispatch({ name: 'items', newValue: items, oldValue: items });
        }
    }
}


export class DropAreaView extends View<DropArea> {

    public constructor(template: string = dropAreaTemplate) {
        super({ template });
    }
}
